import { useContext, useLayoutEffect } from 'react';

import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import am5themes_Dark from "@amcharts/amcharts5/themes/Dark";
import * as am5plugins_exporting from "@amcharts/amcharts5/plugins/exporting";
import { DataContext } from 'contexts/DataContext';

interface AssessmentListProps {
  assessments: any[];
}

const AssessmentScore = (props: AssessmentListProps) => {
  const dataContext = useContext(DataContext);
  const isDark = !!dataContext.darkMode;

  useLayoutEffect(() => {
    // Create root and apply license
    let root = am5.Root.new("AssessmentRisks");
    root._licenseApplied();

    // Set initial theme
    root.setThemes([
      am5themes_Animated.new(root),
    ]);

    // Create chart
    const chart = root.container.children.push(
      am5xy.XYChart.new(root, {
        layout: root.verticalLayout,
        panX: false,
        panY: false,
        wheelX: "panX",
        wheelY: "zoomX",
        paddingLeft: 0
      })
    );

    // Cursor
    const cursor = chart.set("cursor", am5xy.XYCursor.new(root, {
      behavior: "zoomX"
    }));
    cursor.lineY.set("visible", false);

    // Scrollbar X
    const scrollbarX = am5.Scrollbar.new(root, {
      orientation: "horizontal"
    });
    const scrollBgColor = am5.color(isDark ? 0x444444 : 0xcccccc);
    const scrollHandleColor = am5.color(isDark ? 0x4F4F4F : 0xe0e0e0);
    scrollbarX.get("background")?.setAll({
      fill: scrollBgColor,
      fillOpacity: 1,
    });
    scrollbarX.startGrip.get("background")?.setAll({
      fill: scrollHandleColor,
      stroke: scrollHandleColor,
    });
    scrollbarX.endGrip.get("background")?.setAll({
      fill: scrollHandleColor,
      stroke: scrollHandleColor,
    });
    scrollbarX.thumb.setAll({
      fill: scrollHandleColor,
      stroke: scrollHandleColor,
    });
    chart.set("scrollbarX", scrollbarX);
    chart.bottomAxesContainer.children.push(scrollbarX);

    // Data
    const data = props.assessments as any;

    // Create X axis renderer
    const xRenderer = am5xy.AxisRendererX.new(root, {
      minorGridEnabled: false,
      minGridDistance: 0
    });
    xRenderer.grid.template.setAll({
      visible: false,
      stroke: am5.color(isDark ? 0x666666 : 0x000000),
    });

    // Label settings
    xRenderer.labels.template.setAll({
      rotation: -45,
      centerY: am5.p50,
      centerX: am5.p100,
      paddingRight: 15,
      fontSize: 12,
      fontWeight: "bold",
      fill: am5.color(isDark ? 0xffffff : 0x000000),
    });   
    xRenderer.grid.template.setAll({ visible: false })
    // Create X axis (Category)
    const xAxis = chart.xAxes.push(
      am5xy.CategoryAxis.new(root, {
        categoryField: "name",
        renderer: xRenderer,
        tooltip: am5.Tooltip.new(root, {}),
        startLocation: 0.1,
        endLocation: 0.9
      })
    );
    xAxis.data.setAll(data);
    xAxis.events.once("datavalidated", ev => {
      ev.target.zoomToIndexes(0, 7);
    });

    // Create Y axis renderer with horizontal stripes, no grid lines
    const yRenderer = am5xy.AxisRendererY.new(root, {
      cellStartLocation: 0.1,
      cellEndLocation: 0.9,
      strokeOpacity: 1,
      stroke: am5.color(isDark ? 0x666666 : 0x000000),
    });
    // zebra scura in dark mode
    yRenderer.axisFills.template.setAll({
      visible: true,
      fill: am5.color(isDark ? 0x222222 : 0x000000),
      fillOpacity: isDark ? 0.5 : 0.05,
    });
    yRenderer.grid.template.setAll({ visible: false });
    yRenderer.labels.template.setAll({
      fontSize: 12,
      fontWeight: "bold",
      fill: am5.color(isDark ? 0xffffff : 0x000000),
    });

    // Create Y axis (Value)
    const yAxis = chart.yAxes.push(
      am5xy.ValueAxis.new(root, {
        min: 0,
        extraMax: 0.1,
        renderer: yRenderer
      })
    );

    // Series: Score bars
    const series1 = chart.series.push(
      am5xy.ColumnSeries.new(root, {
        name: "Risk",
        xAxis: xAxis,
        yAxis: yAxis,
        valueYField: "risk_detail",
        categoryXField: "name",
        tooltip: am5.Tooltip.new(root, {
          pointerOrientation: "horizontal",
          labelText: "Risk: {valueY} {info}"
        })
      })
    );

    series1.columns.template.setAll({
      tooltipY: am5.percent(10),
      templateField: "columnSettings"
    });
    series1.data.setAll(data);

    // Color adapter
    const colors = [
      "#ffffff",
      "#2AC833",
      "#A7D73F",
      "#FED001",
      "#E67517",
      "#DD1B1D"
    ];

    series1.columns.template.adapters.add("fill", (_fill, target) => {
      const item = target.dataItem?.dataContext as any;
      return am5.color(colors[Math.ceil(item.risk_detail)]);
    });
    series1.columns.template.adapters.add("stroke", (_stroke, target) => {
      const item = target.dataItem?.dataContext as any;
      return am5.color(colors[Math.ceil(item.risk_detail)]);
    });

    // Exporting plugin
    const exporting = am5plugins_exporting.Exporting.new(root, {
      menu: am5plugins_exporting.ExportingMenu.new(root, {}),
      backgroundColor: am5.color("#fff"),
      backgroundOpacity: 1,
      filePrefix: "RiskByAssessment",
    });
    exporting.events.on("exportstarted", () => {
      root.setThemes([]);
      scrollbarX.set("visible", false);
    });
    exporting.events.on("exportfinished", () => {
      root.setThemes([
        am5themes_Animated.new(root),
        am5themes_Dark.new(root)
      ]);
      scrollbarX.set("visible", true);
    });

    // Animations
    chart.appear(1000, 100);
    series1.appear();

    // Cleanup on unmount
    return () => {
      root.dispose();
    };
  }, [props.assessments, isDark]);

  return (
    <div id="AssessmentRisks" style={{ width: "100%", height: "473px" }}></div>
  );
};

export default AssessmentScore;
